import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  AccountService,
  ConfigService,
  CrispChatService,
  LanguageService,
  MetaService,
  ProfileService,
  UtilsService,
} from '@app/core/services';
import { LibrarySidebarService } from '@app/library/sidebars/library-sidebar/library-sidebar.service';
import { LibraryRouter } from '@app/models/library.router';
import { CLibraryFilterData } from '@app/store/models';
import { getLibraryRouteData } from '@app/store/reducers/library.reducer';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { getUserServiceCategories } from '@store/reducers/content.reducer';
import { getMe } from '@store/reducers/profile.reducer';
import {
  EContentModeType,
  EContentPaneFormat,
  EContentPanelRoute,
  FindContentStrategyEnum,
  IContentPane,
  IHash,
  IImageFile,
  Language,
  Router as ContentRouter,
  Subject as BaseSubject,
  User,
  UserRoleEnum,
  UserServiceCategory,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { combineLatest, filter, map, takeUntil } from 'rxjs';
import { i18nService as TranslateService } from '../../../i18n/translate.service';

@Component({
  selector: 'app-library-header',
  templateUrl: './library-header.component.html',
  styleUrls: ['./library-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LibraryHeaderComponent extends ChangableComponent implements OnInit, OnDestroy {
  deviceService = inject(DeviceDetectorService);
  utilsService = inject(UtilsService);
  private crispChat = inject(CrispChatService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly meta = inject(MetaService);
  private readonly configService = inject(ConfigService);
  private readonly translate = inject(TranslateService);
  private readonly sidebarService = inject(LibrarySidebarService);
  private readonly languageService = inject(LanguageService);
  protected profileService = inject(ProfileService);

  public hasSegment = AccountService.hasSegment;
  public imageUrl: string;
  public titles = [];
  public baseTitle = '';
  public metaImageId: string;
  public panesList: IContentPane[];
  public scrollThreshold = 125;
  public isPanes: boolean;
  public meetingFormOpen: boolean;
  public schoolFormOpen: boolean;
  public meetingRoute = ContentRouter.meetingRoute;
  public meetingJoinRoute = ContentRouter.meetingJoinRoute;

  private contentFilter: CLibraryFilterData;
  private libRouter = new LibraryRouter();
  private subjects: BaseSubject[] = [];
  private _userServiceCategories: UserServiceCategory[];
  private defaultImageUrl = null;
  private _currentLanguage: Language;
  private _languages: Language[];
  private _me: User;

  ngOnInit(): void {
    this.imageUrl = this.defaultImageUrl;
    this.sidebarService.enableFilter();
    combineLatest([
      this.store.select(getLibraryRouteData).pipe(filter((v) => !!v)),
      this.configService.subjects$.pipe(filter((v) => !!v)),
      this.store.select(getUserServiceCategories).pipe(filter((v) => !!v)),
      this.languageService.language$.pipe(filter((v) => !!v)),
      this.configService.languages$.pipe(filter((v) => !!v)),
      this.store.select(getMe).pipe(filter((v) => !!v)),
    ])
      .pipe(
        map(([contentFilter, subjects, userServiceCategories, currentLanguage, languages, me]) => {
          this.contentFilter = contentFilter;
          this.subjects = subjects;
          this._userServiceCategories = userServiceCategories;
          this._currentLanguage = currentLanguage;
          this._languages = languages;
          this._me = me;
          return me;
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: () => {
          this.prepare();
          this.updateMeta();

          this.libRouter.setBaseRoute(this.contentFilter.baseRoute);
          this.libRouter.setContentMode(this.contentFilter.contentMode);
          this.libRouter.setContentTypes(this.contentFilter.contentTypeCodes);
          this.libRouter.setSubjects(this.contentFilter.subjectCodes);
          // Temporary hide for classes https://app.clickup.com/t/85zrxkyav
          if (this.contentFilter.baseRoute !== EContentPanelRoute.classes) {
            this.panesList = this.setPaneList();
          }
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public get isTeacher(): boolean {
    return AccountService.isAsIfTeacher(this._me);
  }

  public get isBackVisible(): boolean {
    return !!this.contentFilter?.subjectCodes?.length || !!this.contentFilter?.contentTypeCodes?.length;
  }

  public get backRouteUrl(): string {
    return '/' + this.libRouter.getUpLink().path;
  }

  public get isTutorsPage(): boolean {
    return this.contentFilter.baseRoute === EContentPanelRoute.tutors;
  }

  public onCreateTitle(): string {
    switch (this.contentFilter.baseRoute) {
      case EContentPanelRoute.lessons:
        return 'my-library.actions.create-lesson';
      case EContentPanelRoute.tutors:
        return 'library.header.actions.teachers';
      case EContentPanelRoute.classes:
        return 'library.header.actions.study';
      case EContentPanelRoute.collections:
        return 'my-library.actions.create-collection';
      case EContentPanelRoute.schools:
        return 'school-header.actions.create';
      default:
        return null;
    }
  }

  public onCreated() {
    let url = [];
    let openInNewTab = false;
    switch (this.contentFilter.baseRoute) {
      case EContentPanelRoute.lessons:
        url = ['constructor', 'edit', 'new', 'constructor', 'new'];
        openInNewTab = true;
        break;
      case EContentPanelRoute.tutors:
        url = ['me', 'teacher'];
        break;
      case EContentPanelRoute.classes:
        this.onToggleMeetingForm();
        break;
      case EContentPanelRoute.schools:
        if (this.isDevTester()) {
          this.onToggleSchoolForm();
        } else {
          this.crispChat.openChat();
        }
        break;
      case EContentPanelRoute.collections:
        url = ['content', 'collections', 'edit', 'new', 'constructor', 'new'];
        openInNewTab = true;
        break;
      default:
        url = [];
        break;
    }
    if (openInNewTab) {
      window.open(url.join('/'));
    } else {
      this.router.navigate(url).catch();
    }
  }

  public isDevTester() {
    return this._me.roles.includes(UserRoleEnum.developer) || this._me.roles.includes(UserRoleEnum.tester);
  }

  public onToggleMeetingForm() {
    this.meetingFormOpen = !this.meetingFormOpen;
    this.detectChanges();
  }

  public onToggleSchoolForm() {
    this.schoolFormOpen = !this.schoolFormOpen;
    this.detectChanges();
  }

  public loadFinished(pane, e) {
    this.isPanes = e;

    this.setTimeout(() => {
      this.scrollThreshold = 125;
    }, 300);
  }
  public canBreadcrumbs(): boolean {
    switch (this.contentFilter?.baseRoute) {
      case EContentPanelRoute.main:
      case EContentPanelRoute['people-news']:
      case EContentPanelRoute['classroom-news']:
      case EContentPanelRoute.comments:
      case EContentPanelRoute['school-news']:
        return false;
      default:
        return true;
    }
  }

  private prepare() {
    if (!this.contentFilter) {
      return;
    }
    let titles: string[] = [];
    const images: Array<IHash<IImageFile>> = [];
    const metaImages: string[] = [];
    if (this.contentFilter?.baseRoute) {
      this.baseTitle = `${this.contentFilter?.baseRoute}.page-header-title`;
    }

    if (this.contentFilter.subjectCodes?.length > 0) {
      for (const ss of this.contentFilter.subjectCodes) {
        const subj = this.subjects.find((s) => s.code === ss);
        if (subj) {
          const title = subj.title || '';
          if (title) {
            titles.push(titles.length > 0 ? `, ${title}` : title);
          }
          if (subj.banner) {
            images.push(subj.banner);
          }
          if (subj.cover) {
            metaImages.push(subj.cover_id);
          }
        }
      }
    }
    if (this.contentFilter.contentTypeCodes?.length > 0) {
      if (titles.length > 0) {
        titles.push(' - ');
      }
      const ctTitles: string[] = [];
      let title = [];

      switch (this.contentFilter.baseRoute) {
        case EContentPanelRoute.classes:
          title = this.convertUserServiceCategoriesToTitles(this.contentFilter.contentTypeCodes);
          break;
        default:
          title = LibraryRouter.convertContentTypesToFilter(this.contentFilter.contentTypeCodes).map((_s) =>
            this.translate.directInstant(_s.title),
          );
      }
      if (title) {
        ctTitles.push(title.join(', '));
      }
      titles = titles.concat(ctTitles);
    }

    if (titles.length > 0) {
      titles = [titles.join('')];
    }
    this.titles = titles;

    // TODO: remove it after added image
    const baseRoute = this.contentFilter?.baseRoute.toString().split('/')[0];
    if (['people-news', 'classroom-news', 'comments', 'school-news'].includes(baseRoute)) {
      this.imageUrl = this.assetsUrl('/assets/images/headers/main.jpg');
    } else {
      this.imageUrl =
        images.length && images[0].lg ? this.assetsUrl(images[0].lg.url) : this.setImageUrl() || this.defaultImageUrl;
    }

    if (metaImages.length && metaImages[0]) {
      this.metaImageId = metaImages[0];
    }
  }

  private setImageUrl(): string {
    const baseRoute = this.contentFilter?.baseRoute.toString().split('/')[0];
    return this.assetsUrl(
      ['/assets/images/headers', this.deviceService.isMobile() ? `${baseRoute}_mobile` : baseRoute].join('/') + '.jpg',
    );
  }

  private setPaneList(): IContentPane[] {
    const subjectIds = this.subjects
      .filter((_s) => !!this.contentFilter?.subjectCodes.find((_sc) => _sc === _s.code))
      .map((_s) => _s.id);
    const languageIds = this._languages
      .filter((_l) => !!this.contentFilter?.languageCodes.find((_lc) => _lc === _l.code))
      .map((_l) => _l.id);

    return [
      {
        format: EContentPaneFormat.categories,
        order: 0,
        loaded: false,
        hidden: false,
        directoriesFilter: {
          baseRoute: this.contentFilter?.baseRoute,
          use: FindContentStrategyEnum.library,
          language_id: languageIds,
          subject_id: subjectIds,
          locale: this._currentLanguage?.code,
        },
        displayMode: 'simple',
      },
    ];
  }

  private updateMeta() {
    const titleParts = [];
    const route = this.contentFilter?.baseRoute || 'lessons';
    switch (this.contentFilter.contentMode) {
      case EContentModeType.all:
        // titleParts.push();
        break;

      case EContentModeType.my:
        titleParts.push(`${route}.meta-mine-title`);
        break;

      case EContentModeType.favorites:
        titleParts.push(`${route}.meta-favorites-title`);
        break;

      case EContentModeType.popular:
        titleParts.push(`${route}.meta-popular-title`);
        break;

      case EContentModeType['last-added']:
        titleParts.push(`${route}.meta-recently-created-title`);
        break;

      case EContentModeType['last-viewed']:
        titleParts.push(`${route}.meta-last-viewed-title`);
        break;
    }

    const metaTitles = [...titleParts, ...this.titles, `${route}.meta-title`].filter((s) => s !== '-');

    this.meta.setTitle(metaTitles);
    this.meta.setKeywords(metaTitles);
    this.meta.setDescriptions(metaTitles, '-');
    if (this.metaImageId) {
      this.meta.setImage(this.metaImageId);
    }
  }

  private convertUserServiceCategoriesToTitles(userServiceCategories: string[]) {
    return userServiceCategories.map((category) => this._userServiceCategories.find((c) => c.code === category)?.title);
  }

  public trackByFn(index, pane: IContentPane) {
    return pane.format;
  }
}
